'use client'

import { ReactNode, useEffect, useState } from 'react'
import { InfoBanner } from '@vinted/web-ui'

import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '@marketplace-web/browser/browser-storage-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'

import {
  Conversation,
  TransactionSide,
  EscrowBannerTrackingType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  userMessageThreadBannerClickLinkEvent,
  userMessageThreadBannerCloseEvent,
  userMessageThreadBannerViewEvent,
} from '@marketplace-web/messaging/tracking-data'
import { ESCROW_HELP_URL } from '@marketplace-web/messaging/navigation-feature'

import { HIGH_DETRACTOR_SEGMENT_ID } from '../../constants/constants'

type Props = {
  conversation?: Conversation
}

enum EscrowBannerType {
  Warning = 'warning',
  Info = 'info',
}

const EscrowBanner = ({ conversation }: Props) => {
  const translate = useTranslate('conversation.escrow_banner')
  const { track } = useTracking()
  const userId = useSession().user?.id

  const [isBannerClosed, setIsBannerClosed] = useState(false)

  const conversationId = conversation?.id
  const userSide = conversation?.transaction?.userSide
  const isBuyer = userSide === TransactionSide.Buyer
  const isGermanyMarket = conversation?.germanyBannerMetadata
  const isUserHighDetractor = isBuyer
    ? conversation?.germanyBannerMetadata?.buyerSegment === HIGH_DETRACTOR_SEGMENT_ID
    : conversation?.germanyBannerMetadata?.sellerSegment === HIGH_DETRACTOR_SEGMENT_ID

  const IS_GERMANY_ESCROW_BANNER_SEEN_KEY = `is_germany_escrow_banner_seen-${String(conversationId)}`
  const BANNER_CATEGORY = 'non_escrow_banner'

  const trackingEventData = {
    transactionId: String(conversation?.transactionId),
    userId: String(userId),
    userSide: userSide || null,
    bannerCategory: BANNER_CATEGORY,
    bannerType: EscrowBannerTrackingType.Warning,
  }

  useEffect(() => {
    if (
      !isGermanyMarket ||
      isBannerClosed ||
      getSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY) === 'true'
    )
      return

    track(userMessageThreadBannerViewEvent(trackingEventData))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGermanyMarket, isBannerClosed, track])

  const closeBanner = () => {
    setIsBannerClosed(true)
    setSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY, 'true')

    track(userMessageThreadBannerCloseEvent(trackingEventData))
  }

  const trackLinkClick = () => track(userMessageThreadBannerClickLinkEvent(trackingEventData))

  if (getSessionStorageItem(IS_GERMANY_ESCROW_BANNER_SEEN_KEY) === 'true') return null
  if (!isGermanyMarket || !userSide || isBannerClosed) return null

  const translationKeyPrefix = isUserHighDetractor
    ? `${userSide}.high_detractor`
    : `${userSide}.${EscrowBannerType.Warning}`

  return (
    <InfoBanner
      testId="escrow-banner"
      title={translate(`${translationKeyPrefix}.title`)}
      body={translate(`${translationKeyPrefix}.body`, {
        'faq-link': (chunks: Array<ReactNode>) => (
          <a
            key={ESCROW_HELP_URL}
            href={ESCROW_HELP_URL}
            target="_blank"
            rel="noreferrer"
            onClick={trackLinkClick}
          >
            {chunks}
          </a>
        ),
      })}
      closable
      type="warning"
      onClose={closeBanner}
    />
  )
}

export default EscrowBanner
